import React, { useContext, useEffect, useState } from 'react';
import {
  InputField,
  SelectField,
  CheckboxField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import { states } from '../FONASUCustomerInformation/util';
import styles from './styles.css';
import { FlowContext } from '../../core/flow-engine';
import {
  stateValidation,
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  cityValidation,
  zipCodeValidation
} from '../../flows/shared/validations';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

export const RenderBox = ({ additionalCustomers, primaryState }) => {
  const {
    formProps: {
      minorFirstName,
      minorLastName,
      minorSameAddress,
      minorStreet1,
      minorStreet2,
      minorZip,
      minorCity,
      minorState: minorResidentialState,
      minorGoverningState
    },
    appState: {
      store: {
        app: { governingState }
      }
    }
  } = useContext(FlowContext);

  const [minorState, setMinorState] = useState(
    additionalCustomers[0]?.residentialAddress?.state
  );

  const [enableStateGovernance, setEnableStateGovernance] = useState(
    Boolean(governingState)
  );

  useEffect(() => {
    return primaryState !== minorState
      ? setEnableStateGovernance(true)
      : setEnableStateGovernance(false);
  }, [primaryState]);

  const verifyStateGoverningLogic = ({ target: { value } }) => {
    if (primaryState !== value) {
      setEnableStateGovernance(true);
      setMinorState(value);
    } else {
      setEnableStateGovernance(false);
      setMinorState(null);
    }
  };

  const sameAddressUndefined =
    typeof additionalCustomers[0]?.sameAddress === 'undefined';

  const initialState = sameAddressUndefined
    ? true
    : additionalCustomers[0]?.sameAddress;
  const [sameAddressChecked, toggleSameAddressChecked] = useState(initialState);

  const defaults = {
    people: additionalCustomers.map(person => ({
      [minorFirstName.name]: person?.name?.firstName,
      [minorLastName.name]: person?.name?.lastName,
      [minorSameAddress.name]: person?.sameAddress,
      [minorStreet1.name]: person?.residentialAddress?.street1,
      [minorStreet2.name]: person?.residentialAddress?.street2,
      [minorZip.name]: person?.residentialAddress?.zip,
      [minorCity.name]: person?.residentialAddress?.city,
      [minorResidentialState.name]: person?.residentialAddress?.state,
      [minorGoverningState.name]:
        person?.residentialAddress?.minorGoverningState
    }))
  };

  const { sections } = useRepeatedSection('people', defaults);

  return (
    <div className="sections">
      {sections.map(({ key, getDefault, makeName }, idx) => {
        const { firstName = '', lastName = '' } =
          additionalCustomers[idx]?.name || {};
        return (
          <div key={key} className="section">
            <div className="review-section">
              <CheckboxField
                title={`Use same contact information for ${firstName} ${lastName}`}
                {...minorSameAddress}
                name={makeName(minorSameAddress.name)}
                data-name={generateGeneralTestID(
                  'MinorCI',
                  'MinorSameAddressCheckbox'
                )}
                defaultChecked={sameAddressChecked}
                onClick={() => toggleSameAddressChecked(!sameAddressChecked)}
              />

              {!sameAddressChecked && (
                <div className="address-wrapper">
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Residential / Legal Address Line 1"
                        {...minorStreet1}
                        name={makeName(minorStreet1.name)}
                        defaultValue={getDefault(minorStreet1.name)}
                        validation={[
                          addressLine1Validation,
                          poBoxLine1Validation
                        ]}
                        maxLength="35"
                        className="blocked_data"
                        data-name={generateGeneralTestID(
                          'MinorCI',
                          'MinorResidentialStreetOne'
                        )}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Residential / Legal Address Line 2"
                        {...minorStreet2}
                        name={makeName(minorStreet2.name)}
                        defaultValue={getDefault(minorStreet2.name)}
                        validation={[
                          addressLine2Validation,
                          poBoxLine2Validation
                        ]}
                        maxLength="35"
                        className="blocked_data"
                        data-name={generateGeneralTestID(
                          'MinorCI',
                          'MinorResidentialStreetTwo'
                        )}
                      />
                    </div>
                  </div>
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="City"
                        {...minorCity}
                        name={makeName(minorCity.name)}
                        defaultValue={getDefault(minorCity.name)}
                        validation={cityValidation}
                        className="blocked_data"
                        data-name={generateGeneralTestID(
                          'MinorCI',
                          'MinorResidentialCity'
                        )}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Zip Code"
                        {...minorZip}
                        name={makeName(minorZip.name)}
                        defaultValue={getDefault(minorZip.name)}
                        validation={zipCodeValidation}
                        maxLength="5"
                        className="blocked_data"
                        data-name={generateGeneralTestID(
                          'MinorCI',
                          'MinorResidentialZip'
                        )}
                      />
                    </div>
                    <div className="qv-col">
                      <SelectField
                        title="State"
                        {...minorResidentialState}
                        name={makeName(minorResidentialState.name)}
                        defaultValue={getDefault(minorResidentialState.name)}
                        validation={stateValidation}
                        onChange={verifyStateGoverningLogic}
                        data-name={generateGeneralTestID(
                          'MinorCI',
                          'MinorResidentialState'
                        )}>
                        <option hidden value="">
                          State
                        </option>
                        {states.map(item => (
                          <option
                            key={item}
                            value={item}
                            data-name={generateGeneralTestID(
                              'MinorCI',
                              `State_${item}`
                            )}>
                            {item}
                          </option>
                        ))}
                      </SelectField>
                    </div>
                  </div>

                  {enableStateGovernance && (
                    <div className="qv-row">
                      <div className="qv-col">
                        <SelectField
                          title="Governing state"
                          validation={stateValidation}
                          {...minorGoverningState}
                          name={makeName(minorGoverningState.name)}
                          defaultValue={governingState}
                          data-name={generateGeneralTestID(
                            'MinorCI',
                            'MinorGoverningState'
                          )}>
                          <option hidden value="">
                            Select
                          </option>
                          <option
                            value={primaryState}
                            data-name={generateGeneralTestID(
                              'MinorCI',
                              'PrimaryGoverningState'
                            )}>
                            {primaryState} (my state of residence)
                          </option>
                          <option
                            value={minorState}
                            data-name={generateGeneralTestID(
                              'MinorCI',
                              'MinorGoverningState'
                            )}>
                            {minorState} (minor state of residence)
                          </option>
                        </SelectField>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <style jsx>{styles}</style>
    </div>
  );
};

const MinorCISection = ({ primaryState }) => {
  const {
    appState: {
      store: { additionalCustomers }
    }
  } = useContext(FlowContext);

  return (
    <>
      <div className="section-header">
        <SectionHeading text="Minor" />
      </div>
      <RenderBox
        additionalCustomers={additionalCustomers}
        primaryState={primaryState}
      />
      <style jsx>{styles}</style>
    </>
  );
};

export default MinorCISection;
